import { Box, Grid, Paper, TextField, Typography, useTheme, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ChangeEvent, FC, useContext, useEffect, useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Dot } from 'recharts';
import Barcode from 'react-barcode';

// Extend Window interface for our custom handler
declare global {
  interface Window {
    detailsHandlePrintShelfLabel?: () => void;
  }
}

import { Item as ItemOM } from '../../../app/services/api/orderManagementClient';
import { Item, StockItem as ItemIM } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { useProductDetailsContext } from '../context/ProductDetailsContext';
import { applyRoundAndFormat } from '../../../utils';
import { useSearchParams } from 'react-router-dom';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';

type DetailsProps = {
  imData?: ItemIM;
  omData?: ItemOM;
  currencyCode?: string;
  onPrintShelfLabel?: () => void;
}

const Details: FC<DetailsProps> = ({ imData, omData, currencyCode, onPrintShelfLabel }) => {
  // If parent passes handler, use it; otherwise use internal
  const effectiveHandlePrintShelfLabel = onPrintShelfLabel || (() => { });
  const { t } = useTranslation('productDetails');
  const { minimumQuantity, setMinimumQuantity, isEditMode, isValid, setIsValid } = useProductDetailsContext();
  const [maxShelfLifeDays, setMaxShelfLifeDays] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const isOrderable = searchParams.get('orderable') === 'true';
  const [stockData, setStockData] = useState<Item>();
  const { getByItemNumber } = useNswagClient();
  const { selectedStore, user } = useContext(UserContext);
  const sku = searchParams.get('sku');
  const [showModal, setShowModal] = useState(false);
  const [currentSalePrice, setCurrentSalePrice] = useState<number>(0);
  const currentMonth = new Date().toLocaleString('default', { month: 'short' });
  const [highlightedMonth, setHighlightedMonth] = useState<string>('Nov');

  const isOrderlyUser = user?.emailAddress?.toLowerCase()?.indexOf('@orderly.io') || -1 > -1;

  const theme = useTheme();

  // Mock price history data - in production, this would come from an API
  // Using useMemo to generate data once and prevent constant regeneration
  const priceHistoryData = useMemo(() => {
    const basePrice = imData?.costPrice ?? omData?.price ?? 10;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data = [];

    for (let i = 11; i >= 0; i--) {
      const monthIndex = (new Date().getMonth() - i + 12) % 12;
      const variance = (Math.random() - 0.5) * 2; // +/- 10%
      data.push({
        month: months[monthIndex],
        price: basePrice * (1 + variance / 10),
      });
    }

    return data;
  }, [imData?.costPrice, omData?.price]);

  useEffect(() => {
    getItem(sku ?? '');
    // Initialize sale price to November's value from history
    const novemberData = priceHistoryData.find(d => d.month === 'Nov');
    const initialPrice = novemberData?.price ?? omData?.price ?? 0;
    setCurrentSalePrice(initialPrice);
  }, [sku, imData, omData, stockData]);

  // Expose handlePrintShelfLabel to parent
  useEffect(() => {
    if (onPrintShelfLabel) {
      // Parent can now call this handler
      window.detailsHandlePrintShelfLabel = handlePrintShelfLabel;
    }
  }, [onPrintShelfLabel]);

  const onQuantityChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.preventDefault();
    const value = event.target.value;
    if (!isNaN(Number(value))) {
      setMinimumQuantity(value);
      setIsValid(!(value === '0' || value === ''));
    }
  };

  const getItem = async (sku: string): Promise<void> => {
    const itemResponse = await getByItemNumber(sku, selectedStore?.storeNumber);
    setStockData(itemResponse.data);
  };

  const handlePrintShelfLabel = () => {
    // Trigger workflow to update POS pricing
    console.log('Triggering POS price update workflow for SKU:', sku);
    console.log('Current price:', imData?.costPrice ?? omData?.price);

    // Get the latest price from history
    const latestPrice = priceHistoryData[priceHistoryData.length - 1].price;
    setCurrentSalePrice(latestPrice);

    // Ensure highlighted month is current month
    setHighlightedMonth(currentMonth);

    // Show modal instead of browser alert
    setShowModal(true);
  };

  // Custom dot to highlight current price
  const CustomDot = (props: { cx?: number; cy?: number; payload?: { month: string } }) => {
    const { cx, cy, payload } = props;
    const isHighlighted = payload?.month === highlightedMonth;

    return (
      <Dot
        cx={cx}
        cy={cy}
        r={isHighlighted ? 8 : 4}
        fill={isHighlighted ? theme.palette.secondary.main : theme.palette.primary.main}
        stroke={isHighlighted ? theme.palette.secondary.dark : theme.palette.primary.main}
        strokeWidth={isHighlighted ? 3 : 0}
      />
    );
  };

  return (
    <>
      <Paper sx={{ backgroundColor: theme.palette.primary.main, borderRadius: '5px', width: '100%', height: '100%', my: 5, px: '32px', py: '32px', pt: '16px' }}>
        <Typography
          variant='textMD'
          fontWeight='bold'
          component='div'
          color={theme.palette.common.white}
          sx={{ pb: '16px' }}
        >
          {imData?.description ?? omData?.name}
        </Typography>
        <Grid container
          spacing={8}
          color={theme.palette.common.white}>
          <Grid item>
            <Box
              height='200px'
              width='200px'
              border={`1px solid ${theme.palette.common.white}`}
              sx={{
                backgroundColor: (omData?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                borderRadius: '5px',
                backgroundImage: `url(${omData?.thumbnailUrl ?? ''})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                imageRendering: '-webkit-optimize-contrast',
                flexShrink: 0,
                flexGrow: 0,
              }}
            >
            </Box>
          </Grid>
          <Grid container
            item
            width={'auto'}
            flexGrow={1}
            justifyContent={'stretch'}
            color={theme.palette.common.white}>
            <Box>
              <InfoDetail label={isOrderable ? t('sku') : t('itemNumber')}
                value={imData?.itemNumber ?? omData?.sku ?? t('notAvailable')} />
              <InfoDetail label={t('soh')}
                value={omData?.stockOnHand ?? t('notAvailable')} />
              <InfoDetail label={t('category')}
                value={imData?.primaryCategory ?? omData?.category ?? t('notAvailable')} />
              <InfoDetail label={t('unitCostPrice')}
                value={applyRoundAndFormat(imData?.costPrice ?? omData?.price ?? 0, currencyCode)} />
              <InfoDetail label={t('salePrice')}
                value={applyRoundAndFormat(currentSalePrice || omData?.price || 0, currencyCode)} />
            </Box>
            <Box>
              <InfoDetail label={t('orderableUOM')}
                value={stockData?.pickPackUom ?? stockData?.uom ?? t('notAvailable')} />
              <InfoDetail label={t('primaryUOM')}
                value={omData?.primaryUom ?? imData?.uom ?? t('notAvailable')} />
              <InfoDetail label={t('primaryPerShippableUOM')}
                value={omData?.packSize ?? t('notAvailable')} />
              <InfoDetail label={t('productType')}
                value={stockData?.productType ?? t('notAvailable')} />
            </Box>
          </Grid>
          <Grid
            item
            sx={{ ml: 'auto', width: '500px' }}>
            <Typography
              variant='textSM'
              fontWeight='bold'
              sx={{ mb: 1 }}>
              {t('priceHistory')}
            </Typography>
            <ResponsiveContainer
              width="100%"
              height={180}>
              <LineChart data={priceHistoryData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.palette.custom.gray[300]} />
                <XAxis
                  dataKey="month"
                  stroke={theme.palette.common.white}
                  style={{ fontSize: '10px' }}
                />
                <YAxis
                  stroke={theme.palette.common.white}
                  style={{ fontSize: '10px' }}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.custom.gray[300]}`,
                    borderRadius: '4px',
                  }}
                  formatter={(value: number) => [applyRoundAndFormat(value, currencyCode), 'Price']}
                />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke={theme.palette.common.white}
                  strokeWidth={2}
                  dot={<CustomDot />}
                  activeDot={{ r: 6 }}
                  isAnimationActive={true}
                  animationDuration={800}
                  animationBegin={0}
                />
              </LineChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      </Paper>

      {/* Modal for Shelf Label Confirmation */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('shelfLabelSuccess')}</DialogTitle>
        <DialogContent>
          <Typography
            variant="textMD"
            sx={{ mb: 2 }}>
            {t('shelfLabelMessage')}
          </Typography>

          {/* Shelf Edge Label Preview */}
          <Box
            sx={{
              border: `2px solid ${theme.palette.custom.gray[900]}`,
              borderRadius: '4px',
              padding: '16px',
              backgroundColor: theme.palette.common.white,
              my: 3,
              textAlign: 'center',
              maxWidth: '400px',
              mx: 'auto',
            }}>
            <Typography
              variant="textSM"
              sx={{
                fontWeight: 'bold',
                mb: 1,
                fontSize: '12px',
                textTransform: 'uppercase',
                color: theme.palette.custom.gray[700],
              }}>
              {imData?.description ?? omData?.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '48px',
                fontWeight: 'bold',
                lineHeight: 1,
                color: theme.palette.primary.main,
                mb: 1,
              }}>
              {applyRoundAndFormat(currentSalePrice, currencyCode)}
            </Typography>
            <Typography
              variant="textSM"
              sx={{
                fontSize: '10px',
                color: theme.palette.custom.gray[600],
                mb: 2,
              }}>
              {sku} • {imData?.uom ?? omData?.primaryUom ?? 'Unit'}
            </Typography>

            {/* Barcode */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
              }}>
              <Barcode
                value={omData?.barcode ?? imData?.itemNumber ?? sku ?? '0000000000'}
                width={1.5}
                height={50}
                fontSize={11}
                margin={0}
                displayValue={true}
              />
            </Box>
          </Box>

          <Typography
            variant="textSM"
            color="text.secondary"
            sx={{ mt: 2 }}>
            {imData?.description ?? omData?.name} ({sku})
          </Typography>
          <Typography
            variant="textSM"
            color="text.secondary">
            {t('salePrice')}: {applyRoundAndFormat(currentSalePrice, currencyCode)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowModal(false)}
            variant="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container
        py={5}
        px={10}
        direction='column'
        xs={12}
        md={5}
        lg={5}
        rowGap={5}
      >
        <Grid item>
          <InfoDetail label={t('bomEnabled')}
            value={imData?.isBom ? t('enabled') : t('notEnabled')}
          />
        </Grid>
        <Grid item>
          <InfoDetail label={t('posEnabled')}
            value={imData?.isPosEnabled ? t('enabled') : t('notEnabled')}
          />
        </Grid>
        <Grid item>
          <InfoDetail label={t('stockItem')}
            value={imData?.isStockItem ? t('enabled') : t('notEnabled')}
          />
        </Grid>
        <Grid item>
          <InfoDetail label={t('saleItem')}
            value={stockData?.isSaleItem ? t('enabled') : t('notEnabled')}
          />
        </Grid>
        <Grid item>
          <InfoDetail label={t('status')}
            value={stockData?.status ?? t('notAvailable')}
          />
        </Grid>
        <Grid item>
          {isEditMode ? (
            <>
              <EditableInfoDetail label={t('minOrderQty')}>
                <TextField
                  size='small'
                  placeholder={imData?.minimumQuantity?.toString() ?? t('minQtyPlaceholder')}
                  sx={{ pl: 2 }}
                  value={minimumQuantity}
                  onChange={onQuantityChange}
                  error={!isValid}
                  helperText={!isValid ? t('minQuantityValidationMessage') : ''}
                />
              </EditableInfoDetail>
              {isOrderlyUser ?
                <EditableInfoDetail label={t('maxShelfLifeDays')}>
                  <TextField
                    size='small'
                    placeholder={'-'}
                    sx={{ pl: 2 }}
                    value={maxShelfLifeDays ?? ''}
                    onChange={(e) => { setMaxShelfLifeDays(Number.parseFloat(e.target.value)); }}
                    error={maxShelfLifeDays !== null && (isNaN(maxShelfLifeDays) || maxShelfLifeDays < 0)}
                  />
                </EditableInfoDetail> : null}
            </>
          ) : (
            <>
              <InfoDetail label={t('minOrderQty')}
                value={imData?.minimumQuantity ?? Number(0).toFixed(2)}
              />
              {isOrderlyUser ?
                <InfoDetail label={t('maxShelfLifeDays')}
                  value={maxShelfLifeDays ?? Number(0).toFixed(3)}
                /> : null}
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

type InfoDetailProps = {
  label: string;
  value: string | number | boolean;
  height?: string;
}

const InfoDetail = ({ label, value }: InfoDetailProps) => {
  return (
    <Typography variant='textSM'
      component='div'
      p={1}
      paddingBottom={'16px'}>{label}: {value}</Typography>
  );
};

type EditableInfoDetailProps = {
  label: string;
  children: JSX.Element;
  height?: string;
}

const EditableInfoDetail = ({ label, children, height }: EditableInfoDetailProps) => {
  return (
    <Box display='flex'
      height={height}
      flexDirection='row'
      alignItems='baseline'
    >
      <Typography variant='textSM'
        p={1}
      >{label}:</Typography>
      {children}
    </Box>
  );
};

export default Details;